"use client"

import * as React from "react"
import { useActionState } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"

import { addToCartEvent } from "@workspace/analytics"
import { AlertNotice } from "@workspace/ui/components/alert-notice"
import { Button } from "@workspace/ui/components/button"
import { toast } from "@workspace/ui/components/sonner"

import {
  ADD_TO_CART_IDLE,
  type AddToCartState,
} from "../../cart/add-to-cart-state"
import { useAnalytics } from "@/components/analytics/analytics-provider"

import { useMarkAdded } from "./reset-after-add"

/**
 * Client boundary around every add-to-cart `<form>` (standard + all composite
 * builders). Drives the add Server Action through `useActionState` so success
 * no longer hard-redirects to `/cart` — instead it toasts "added · View cart"
 * and `router.refresh()`es (to update the header basket badge), and when
 * rendered inside the US-018 intercepting-route modal (`closeOnAdd`) it
 * `router.back()`s to dismiss the Dialog. That `redirect("/cart")` was what
 * left the modal stranded OPEN over the cart page (a parallel-route quirk:
 * a Server-Action redirect inside an intercepted route doesn't tear down the
 * `@modal` slot). An add error (sold out / gone) opens a blocking
 * `AlertNotice` with the reason instead: the add did NOT happen, and a toast
 * is easy to miss (on a phone, or as the builder modal stays open).
 *
 * The form's inputs/submit button are passed as `children` (server-rendered by
 * each builder), so this stays a thin client shell — and, being a
 * `useActionState` form, still submits + mutates without client JS; only the
 * toast/close niceties are JS-only.
 *
 * DOUBLE-ADD GUARD: an add can take seconds in production (live menu re-fetch
 * + cart cookie write + the post-add `router.refresh()`), and every extra
 * submit queued meanwhile adds ANOTHER line. So the form locks on the first
 * submit (a ref, so a fast double-click/Enter before React re-renders is
 * caught too) and stays locked until the add has fully settled: an error
 * unlocks at once; an add on the full item page unlocks once the refresh has
 * repainted; an add that closes the builder (`closeOnAdd`) stays locked until
 * the route is hidden. {@link AddToCartSubmitButton} reflects the lock.
 */
export function AddToCartForm({
  action,
  itemName,
  closeOnAdd = false,
  id,
  className,
  children,
}: {
  action: (prev: AddToCartState, formData: FormData) => Promise<AddToCartState>
  itemName: string
  closeOnAdd?: boolean
  /** For controls associated from outside the form (`form="<id>"`). */
  id?: string
  className?: string
  children: React.ReactNode
}) {
  const t = useTranslations("customize")
  const router = useRouter()
  const markAdded = useMarkAdded()
  const { track } = useAnalytics()
  const [locked, setLocked] = React.useState(false)
  const lockedRef = React.useRef(false)
  const awaitingRefresh = React.useRef(false)
  const setLock = React.useCallback((value: boolean) => {
    lockedRef.current = value
    setLocked(value)
  }, [])
  const [state, formAction, isActionPending] = useActionState(
    async (prev: AddToCartState, formData: FormData) => {
      const next = await action(prev, formData)
      // Rejected: nothing was added, so the diner may retry straight away.
      if (next.status === "error") setLock(false)
      return next
    },
    ADD_TO_CART_IDLE
  )
  const handled = React.useRef<AddToCartState>(ADD_TO_CART_IDLE)
  const [isRefreshing, startRefresh] = React.useTransition()
  // The error result the diner last dismissed. Each dispatch yields a new
  // state object, so a repeat of the same failure re-opens the notice.
  const [dismissed, setDismissed] = React.useState<AddToCartState | null>(null)
  const errorOpen = state.status === "error" && state !== dismissed

  React.useEffect(() => {
    // Only react to a FRESH action result (each dispatch yields a new object);
    // the ref guard also swallows React StrictMode's double-effect in dev.
    if (state === handled.current) return
    handled.current = state

    if (state.status === "added") {
      markAdded()
      if (state.tracked) track(addToCartEvent(state.tracked))
      toast.success(t("addedToast", { name: itemName }), {
        action: {
          label: t("viewCartAction"),
          onClick: () => router.push("/cart"),
        },
      })
      if (!closeOnAdd) awaitingRefresh.current = true
      startRefresh(() => router.refresh())
      if (closeOnAdd) router.back()
    }
  }, [state, closeOnAdd, itemName, markAdded, router, t, track])

  // Full item page: unlock once the post-add refresh has landed.
  React.useEffect(() => {
    if (isRefreshing || !awaitingRefresh.current) return
    awaitingRefresh.current = false
    setLock(false)
  }, [isRefreshing, setLock])

  // Hidden (navigated away; `cacheComponents` keeps the route mounted inside
  // `<Activity>`) or unmounted: release the lock so a revisit can add again.
  React.useLayoutEffect(() => {
    return () => {
      awaitingRefresh.current = false
      setLock(false)
    }
  }, [setLock])

  return (
    <>
      <AddToCartBusyContext.Provider
        value={locked || isActionPending || isRefreshing}
      >
        <form
          id={id}
          action={formAction}
          className={className}
          onSubmit={(event) => {
            // Cancelling the submit event also stops React dispatching the action.
            if (lockedRef.current) {
              event.preventDefault()
              return
            }
            setLock(true)
          }}
        >
          {children}
        </form>
      </AddToCartBusyContext.Provider>
      <AlertNotice
        open={errorOpen}
        onOpenChange={(open) => {
          if (!open) setDismissed(state)
        }}
        title={t("cartErrorTitle")}
        // Read from `state` even once dismissed, so the text stays put
        // through the close animation.
        description={
          state.status !== "error"
            ? ""
            : state.error === "sold_out"
              ? t("soldOut")
              : t(`cartError.${state.error}`)
        }
        actionLabel={t("alertDismiss")}
      />
    </>
  )
}

const AddToCartBusyContext = React.createContext(false)

/**
 * Submit button for an {@link AddToCartForm}: disabled, with an "Adding…"
 * label, from the first click until the add has settled (see the form's
 * double-add guard). Must render INSIDE the form.
 */
export function AddToCartSubmitButton({
  disabled = false,
  children,
  ...props
}: Omit<React.ComponentProps<typeof Button>, "type">) {
  const t = useTranslations("customize")
  const busy = React.useContext(AddToCartBusyContext)
  return (
    <Button
      type="submit"
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...props}
    >
      {busy ? t("addingToCart") : children}
    </Button>
  )
}
